import { useState, useEffect } from 'preact/hooks';
import type { JSX } from 'preact';
import { authState, sidebarCollapsed, toggleSidebar, SIDEBAR_EXPANDED, SIDEBAR_COLLAPSED } from '../App';
import { api } from '../services/api';

// ── Types ──────────────────────────────────────────────────────────────
interface NavItem { href: string; label: string; eyebrow: string; icon: (active: boolean) => JSX.Element; }
interface SidebarContentProps {
  collapsed: boolean;
  currentPath: string;
  onNavigate?: () => void;
  showCollapse?: boolean;
}

function getCurrentPath() { return window.location.pathname; }

// ── Nav items ──────────────────────────────────────────────────────────
const navItems: NavItem[] = [
  {
    href: '/',
    label: 'Dashboard',
    eyebrow: '01',
    icon: (a) => (
      <svg style={{ width: 18, height: 18, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={a ? 2.5 : 2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    href: '/checkins',
    label: 'Check-ins',
    eyebrow: '02',
    icon: (a) => (
      <svg style={{ width: 18, height: 18, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={a ? 2.5 : 2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  {
    href: '/families',
    label: 'Children',
    eyebrow: '03',
    icon: (a) => (
      <svg style={{ width: 18, height: 18, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={a ? 2.5 : 2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    href: '/team',
    label: 'Team',
    eyebrow: '04',
    icon: (a) => (
      <svg style={{ width: 18, height: 18, flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={a ? 2.5 : 2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
  },
];

function isActive(href: string, path: string) {
  if (href === '/') return path === '/' || path === '/dashboard';
  return path.startsWith(href);
}

// ── Tooltip (collapsed mode) ──────────────────────────────────────────
function NavTooltip({ label, children }: { label: string; children: JSX.Element }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position: 'absolute', left: '100%', marginLeft: 12,
          top: '50%', transform: 'translateY(-50%)', zIndex: 50,
          pointerEvents: 'none',
        }}>
          <div style={{
            background: 'var(--ink)',
            color: 'var(--paper)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            padding: '6px 12px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
            boxShadow: 'var(--shadow-card)',
          }}>
            {label}
          </div>
          <div style={{
            position: 'absolute', right: '100%', top: '50%',
            transform: 'translateY(-50%)',
            borderWidth: 4, borderStyle: 'solid',
            borderColor: 'transparent',
            borderRightColor: 'var(--ink)',
          }} />
        </div>
      )}
    </div>
  );
}

// ── Sidebar Content ──────────────────────────────────────────────────
function SidebarContent({ collapsed, currentPath, onNavigate, showCollapse = true }: SidebarContentProps) {
  const { user, church } = authState.value;

  const handleLogout = async () => {
    try { await api.logout(); } catch (_) { api.clearAuth(); }
    authState.value = { isAuthenticated: false, isLoading: false, user: null, church: null };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Logo / Church name ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid var(--border)',
        padding: collapsed ? '20px 0' : '20px 16px',
        justifyContent: collapsed ? 'center' : 'flex-start',
        gap: 12,
        transition: 'padding 300ms var(--ease-out)',
      }}>
        {/* Marigold brand-mark with Care Atlas avatar radius */}
        <div style={{
          width: 36, height: 36,
          background: 'var(--marigold)',
          borderRadius: '13px 13px 13px 3px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 4px 12px rgba(244, 182, 64, 0.25)',
        }}>
          <svg width="20" height="20" fill="none" stroke="var(--ink)" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round"
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </div>
        {!collapsed && (
          <div style={{ minWidth: 0 }} className="animate-fade-in">
            <p style={{
              fontSize: 14, fontWeight: 700,
              fontFamily: 'var(--font-display)',
              letterSpacing: '-0.04em',
              color: 'var(--ink)',
              margin: 0, lineHeight: 1.2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {church?.name || 'WeShephard'}
            </p>
            <p style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase', color: 'var(--ink-faint)',
              margin: 0, marginTop: 2,
            }}>
              Admin Portal
            </p>
          </div>
        )}
      </div>

      {/* ── Nav items ── */}
      <nav style={{ flex: 1, padding: '16px 8px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!collapsed && (
          <p style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.16em',
            textTransform: 'uppercase', color: 'var(--ink-faint)',
            padding: '0 12px', marginBottom: 8, marginTop: 0,
          }} className="animate-fade-in">
            Navigation
          </p>
        )}
        {navItems.map((item) => {
          const active = isActive(item.href, currentPath);
          const link = (
            <a
              key={item.href}
              href={item.href}
              onClick={onNavigate}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: collapsed ? '10px 0' : '10px 12px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 12,
                textDecoration: 'none',
                cursor: 'pointer',
                userSelect: 'none',
                transition: 'all 160ms var(--ease-out)',
                background: active ? 'var(--marigold)' : 'transparent',
                color: active ? 'var(--ink)' : 'var(--ink-soft)',
                fontWeight: active ? 600 : 400,
                fontSize: 14,
                fontFamily: 'var(--font-body)',
                position: 'relative',
              }}
              onMouseEnter={e => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(25,38,49,0.05)';
                  (e.currentTarget as HTMLElement).style.color = 'var(--ink)';
                }
              }}
              onMouseLeave={e => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = 'var(--ink-soft)';
                }
              }}
            >
              {/* Eyebrow number (expanded only) */}
              {!collapsed && (
                <span style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 10, fontWeight: 600,
                  color: active ? 'var(--ink)' : 'var(--ink-faint)',
                  letterSpacing: '0.02em',
                  minWidth: 18,
                  lineHeight: 1,
                }}>
                  {item.eyebrow}
                </span>
              )}
              {item.icon(active)}
              {!collapsed && <span className="animate-fade-in">{item.label}</span>}
              {/* Active dot */}
              {!collapsed && active && (
                <span style={{
                  marginLeft: 'auto',
                  width: 6, height: 6,
                  borderRadius: '50%',
                  background: 'var(--ink)',
                  opacity: 0.4,
                  flexShrink: 0,
                }} />
              )}
            </a>
          );
          return collapsed
            ? <NavTooltip key={item.href} label={item.label}>{link}</NavTooltip>
            : link;
        })}
      </nav>

      {/* ── Live indicator ── */}
      <div style={{
        borderTop: '1px solid var(--border)',
        padding: '12px 8px',
        display: 'flex',
        justifyContent: collapsed ? 'center' : 'flex-start',
      }}>
        {collapsed ? (
          <NavTooltip label="Children in care — live">
            <div style={{
              width: 36, height: 36, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              borderRadius: 10, cursor: 'default',
            }}>
              <span className="signal-dot signal-dot--live" />
            </div>
          </NavTooltip>
        ) : (
          <div className="animate-fade-in" style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '0 12px',
          }}>
            <span className="signal-dot signal-dot--live" />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: '#2F7A4E' }}>
              Children in care
            </span>
          </div>
        )}
      </div>

      {/* ── User profile ── */}
      <div style={{
        borderTop: '1px solid var(--border)',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
        {collapsed ? (
          <>
            <NavTooltip label={`${user?.name || ''} · Settings`}>
              <a href="/profile" onClick={onNavigate} style={{
                display: 'flex', justifyContent: 'center',
                padding: '8px 0', borderRadius: 10, textDecoration: 'none',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(25,38,49,0.05)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <div style={{
                  width: 32, height: 32,
                  background: 'var(--marigold-soft)',
                  borderRadius: '13px 13px 13px 3px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1.5px solid var(--marigold)',
                }}>
                  {user?.profilePhoto
                    ? <img src={user.profilePhoto} alt={user.name} style={{ width: 32, height: 32, borderRadius: '13px 13px 13px 3px', objectFit: 'cover' }} />
                    : <span style={{ color: '#7D5507', fontWeight: 700, fontSize: 11 }}>{user?.name?.charAt(0) || 'U'}</span>
                  }
                </div>
              </a>
            </NavTooltip>
            <NavTooltip label="Sign Out">
              <button onClick={handleLogout} style={{
                display: 'flex', justifyContent: 'center',
                width: '100%', padding: '8px 0',
                borderRadius: 10, background: 'transparent',
                border: 'none', cursor: 'pointer', color: 'var(--ink-faint)',
                transition: 'all 160ms var(--ease-out)',
              }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--coral-soft)'; (e.currentTarget as HTMLElement).style.color = 'var(--coral)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--ink-faint)'; }}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </NavTooltip>
          </>
        ) : (
          <>
            <a href="/profile" onClick={onNavigate} className="animate-fade-in" style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', borderRadius: 12, textDecoration: 'none',
              transition: 'background 160ms var(--ease-out)',
            }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(25,38,49,0.05)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <div style={{
                width: 34, height: 34, flexShrink: 0,
                background: 'var(--marigold-soft)',
                borderRadius: '13px 13px 13px 3px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1.5px solid var(--marigold)',
              }}>
                {user?.profilePhoto
                  ? <img src={user.profilePhoto} alt={user.name} style={{ width: 34, height: 34, borderRadius: '13px 13px 13px 3px', objectFit: 'cover' }} />
                  : <span style={{ color: '#7D5507', fontWeight: 700, fontSize: 12 }}>{user?.name?.charAt(0) || 'U'}</span>
                }
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <p style={{
                  fontSize: 13, fontWeight: 600, color: 'var(--ink)',
                  margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {user?.name}
                </p>
                <p style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'var(--ink-faint)',
                  margin: 0, marginTop: 1,
                }}>
                  {user?.role}
                </p>
              </div>
              <svg width="14" height="14" fill="none" stroke="var(--ink-faint)" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </a>
            <button onClick={handleLogout} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 12px', width: '100%',
              borderRadius: 12, background: 'transparent',
              border: 'none', cursor: 'pointer',
              fontSize: 13, color: 'var(--ink-faint)',
              fontFamily: 'var(--font-body)',
              transition: 'all 160ms var(--ease-out)',
            }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'var(--coral-soft)';
                (e.currentTarget as HTMLElement).style.color = 'var(--coral)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
                (e.currentTarget as HTMLElement).style.color = 'var(--ink-faint)';
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </button>
          </>
        )}
      </div>

      {/* ── Collapse toggle — desktop only ── */}
      {showCollapse && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '8px' }}>
          <button
            onClick={toggleSidebar}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              width: '100%', display: 'flex', alignItems: 'center',
              padding: collapsed ? '10px 0' : '10px 12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 8, borderRadius: 12, background: 'transparent',
              border: 'none', cursor: 'pointer', color: 'var(--ink-faint)',
              fontFamily: 'var(--font-body)', fontSize: 11,
              fontWeight: 700, letterSpacing: '0.1em',
              transition: 'all 160ms var(--ease-out)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = 'rgba(25,38,49,0.05)';
              (e.currentTarget as HTMLElement).style.color = 'var(--ink-soft)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
              (e.currentTarget as HTMLElement).style.color = 'var(--ink-faint)';
            }}
          >
            <svg
              width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"
              style={{
                flexShrink: 0,
                transition: 'transform 300ms var(--ease-out)',
                transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            {!collapsed && <span className="animate-fade-in">Collapse</span>}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main Navigation component ─────────────────────────────────────────
export function Navigation() {
  const [currentPath, setCurrentPath] = useState(getCurrentPath());
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const collapsed = sidebarCollapsed.value;

  // Track route changes
  useEffect(() => {
    const handler = () => setCurrentPath(getCurrentPath());
    window.addEventListener('popstate', handler);
    const origPush = history.pushState.bind(history);
    history.pushState = (...args) => { origPush(...args); handler(); };
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Track window size and close mobile drawer on resize to desktop
  useEffect(() => {
    const handler = () => {
      const desktop = window.innerWidth >= 768;
      setIsDesktop(desktop);
      if (desktop) setMobileOpen(false);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <>
      {/* ══ Desktop Sidebar (wide screens only) ══ */}
      <aside
        className="hidden md:flex flex-col fixed inset-y-0 left-0 z-40"
        style={{
          width: sidebarWidth,
          background: 'var(--paper)',
          borderRight: '1px solid var(--border)',
          transition: 'width 300ms var(--ease-out)',
          overflow: 'hidden',
          boxShadow: '4px 0 24px rgba(25, 38, 49, 0.06)',
        }}
      >
        <SidebarContent collapsed={collapsed} currentPath={currentPath} showCollapse={true} />
      </aside>

      {/* ══ Mobile Top Bar — ONLY on mobile (< 768px) where sidebar is hidden ══ */}
      {!isDesktop && (
        <div
          className="fixed top-0 left-0 right-0 z-40 flex md:hidden items-center justify-between px-4"
          style={{
            height: 56,
            background: 'var(--paper)',
            borderBottom: '1px solid var(--border)',
            boxShadow: '0 2px 12px rgba(25, 38, 49, 0.07)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, background: 'var(--marigold)',
              borderRadius: '13px 13px 13px 3px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 3px 8px rgba(244, 182, 64, 0.25)',
            }}>
              <svg width="18" height="18" fill="none" stroke="var(--ink)" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 15,
              fontWeight: 600, letterSpacing: '-0.04em', color: 'var(--ink)',
              maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {authState.value.church?.name || 'WeShephard'}
            </span>
          </div>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            style={{
              padding: 8, borderRadius: 10, background: 'transparent',
              border: 'none', cursor: 'pointer', color: 'var(--ink-soft)',
              transition: 'all 160ms var(--ease-out)',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(25,38,49,0.05)'; (e.currentTarget as HTMLElement).style.color = 'var(--ink)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'var(--ink-soft)'; }}
          >
            {mobileOpen
              ? <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              : <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            }
          </button>
        </div>
      )}

      {/* ══ Mobile Drawer (mobile only) ══ */}
      {!isDesktop && mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(25, 38, 49, 0.35)', backdropFilter: 'blur(4px)' }}
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer panel */}
          <aside
            className="fixed top-0 left-0 bottom-0 z-50 flex flex-col animate-slide-in-left"
            style={{
              width: SIDEBAR_EXPANDED,
              background: 'var(--paper)',
              borderRight: '1px solid var(--border)',
              boxShadow: '8px 0 32px rgba(25, 38, 49, 0.15)',
            }}
          >
            {/* Drawer header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px', borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 32, height: 32, background: 'var(--marigold)',
                  borderRadius: '13px 13px 13px 3px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="18" height="18" fill="none" stroke="var(--ink)" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round"
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: 15,
                  fontWeight: 600, letterSpacing: '-0.04em', color: 'var(--ink)',
                }}>
                  WeShephard
                </span>
              </div>
              <button
                onClick={() => setMobileOpen(false)}
                style={{
                  padding: 6, borderRadius: 8, background: 'transparent',
                  border: 'none', cursor: 'pointer', color: 'var(--ink-faint)',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(25,38,49,0.05)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Drawer content */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <SidebarContent
                collapsed={false}
                currentPath={currentPath}
                onNavigate={() => setMobileOpen(false)}
                showCollapse={false}
              />
            </div>
          </aside>
        </>
      )}
    </>
  );
}
